import axios, { AxiosInstance } from 'axios';
import {
  Firm,
  OrgUnit,
  HierarchyLevel,
  HierarchyConfigDefaults,
  HierarchyLevelKey,
  CaseAssumptionsUpdate,
  FirmCaseAssumptionsResponse,
} from '../types/advisor';
import { AdvisorConfig, PartnerWebhookConfig, PartnerWebhookUpdate, WebhookTestResult } from './advisorConfigService';

const API_BASE_URL = import.meta.env.VITE_PLANSCOUT_API_URL || 'http://localhost:8080';

const createAuthorizedClient = (token: string): AxiosInstance =>
  axios.create({
    baseURL: API_BASE_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

// ─── Firms ──────────────────────────────────────────────────────

export const getAllFirms = async (token: string): Promise<Firm[]> => {
  const client = createAuthorizedClient(token);
  const response = await client.get('/api/firms');
  return response.data;
};

export const getFirm = async (token: string, firmId: string, includeTree = false): Promise<Firm> => {
  const client = createAuthorizedClient(token);
  const response = await client.get(`/api/firms/${firmId}${includeTree ? '?include=tree' : ''}`);
  return response.data;
};

export interface CreateFirmInput {
  name: string;
  status?: 'active' | 'inactive';
  hierarchyLevels?: HierarchyLevel[] | HierarchyLevelKey[];
  defaults?: HierarchyConfigDefaults;
  // Create-time only. false = skip the per-firm Auth0 M2M client (for firms whose
  // integrations already use existing per-advisor clients). Default true. Cannot be
  // provisioned later from the dashboard.
  provisionAuth0?: boolean;
}

export const createFirm = async (token: string, input: CreateFirmInput): Promise<Firm> => {
  const client = createAuthorizedClient(token);
  const response = await client.post('/api/firms', input);
  return response.data; // includes m2mClientId + m2mClientSecret ONCE
};

export const updateFirm = async (token: string, firmId: string, updates: Partial<Omit<CreateFirmInput, 'provisionAuth0'>>): Promise<Firm> => {
  const client = createAuthorizedClient(token);
  const response = await client.put(`/api/firms/${firmId}`, updates);
  return response.data;
};

// ─── Firm default automation preferences (enterprise-level) ────────────────
//
// Scoped endpoints (requireFirmScope server-side): admins always; enterprise
// users (orgAssignments on the firm) read+write; advisors under the firm
// read-only (for "reset to enterprise defaults").

export interface FirmAutomationPreferencesResponse {
  firmId: string;
  firmName?: string;
  preferences: Record<string, any>; // SipsAutomationPreferences shape
  source: 'firm' | 'default';
}

export const getFirmAutomationPreferences = async (
  token: string,
  firmId: string
): Promise<FirmAutomationPreferencesResponse> => {
  const client = createAuthorizedClient(token);
  const response = await client.get(`/api/firms/${firmId}/automation-preferences`);
  return response.data;
};

/**
 * The canonical PlanScout-wide default automation preferences — the hardcoded
 * no-skip set, independent of any firm's stored provisioning defaults.
 */
export const getPlanscoutDefaultAutomationPreferences = async (
  token: string
): Promise<Record<string, any>> => {
  const client = createAuthorizedClient(token);
  const response = await client.get('/api/automation-preferences/planscout-defaults');
  return response.data.preferences;
};

export const updateFirmAutomationPreferences = async (
  token: string,
  firmId: string,
  preferences: Record<string, any>
): Promise<FirmAutomationPreferencesResponse> => {
  const client = createAuthorizedClient(token);
  const response = await client.put(`/api/firms/${firmId}/automation-preferences`, preferences);
  return response.data;
};

// ─── Firm case assumptions (asset return rates + COLA) ──────────────────────
//
// Unlike automation preferences these are NOT copied to advisors: they resolve
// live, per key, firm → branch → advisor on every new case. Same requireFirmScope
// access as the automation-preferences endpoints.

export const getFirmCaseAssumptions = async (
  token: string,
  firmId: string
): Promise<FirmCaseAssumptionsResponse> => {
  const client = createAuthorizedClient(token);
  const response = await client.get(`/api/firms/${firmId}/case-assumptions`);
  return response.data;
};

export const updateFirmCaseAssumptions = async (
  token: string,
  firmId: string,
  update: CaseAssumptionsUpdate
): Promise<FirmCaseAssumptionsResponse> => {
  const client = createAuthorizedClient(token);
  const response = await client.put(`/api/firms/${firmId}/case-assumptions`, update);
  return response.data;
};

export const deleteFirm = async (token: string, firmId: string): Promise<void> => {
  const client = createAuthorizedClient(token);
  await client.delete(`/api/firms/${firmId}`);
};

export const rotateFirmSecret = async (token: string, firmId: string): Promise<{ m2mClientId: string; m2mClientSecret: string }> => {
  const client = createAuthorizedClient(token);
  const response = await client.post(`/api/firms/${firmId}/rotate-secret`, {});
  return response.data;
};

// ─── Org units ──────────────────────────────────────────────────

export const getOrgUnits = async (token: string, firmId: string): Promise<OrgUnit[]> => {
  const client = createAuthorizedClient(token);
  const response = await client.get(`/api/firms/${firmId}/org-units`);
  return response.data;
};

export interface CreateOrgUnitInput {
  level: HierarchyLevelKey;
  parentOrgUnitId?: string | null;
  name: string;
  officeCode?: string | null;
  status?: 'active' | 'inactive';
  overrides?: HierarchyConfigDefaults;
}

export const createOrgUnit = async (token: string, firmId: string, input: CreateOrgUnitInput): Promise<OrgUnit> => {
  const client = createAuthorizedClient(token);
  const response = await client.post(`/api/firms/${firmId}/org-units`, input);
  return response.data;
};

export const updateOrgUnit = async (token: string, orgUnitId: string, updates: Partial<CreateOrgUnitInput>): Promise<OrgUnit> => {
  const client = createAuthorizedClient(token);
  const response = await client.put(`/api/org-units/${orgUnitId}`, updates);
  return response.data;
};

export const deleteOrgUnit = async (token: string, orgUnitId: string): Promise<void> => {
  const client = createAuthorizedClient(token);
  await client.delete(`/api/org-units/${orgUnitId}`);
};

// ─── Advisor assignment ─────────────────────────────────────────

export const assignAdvisor = async (
  token: string,
  customerId: string,
  assignment: { firmId: string | null; orgUnitId: string | null }
): Promise<AdvisorConfig> => {
  const client = createAuthorizedClient(token);
  const response = await client.put(`/api/advisor-configs/${customerId}/assignment`, assignment);
  return response.data;
};

export const provisionAdvisorSips = async (token: string, customerId: string): Promise<{ success: boolean; sipsTasId: string; advisor: AdvisorConfig }> => {
  const client = createAuthorizedClient(token);
  const response = await client.post(`/api/advisor-configs/${customerId}/provision-sips`, {});
  return response.data;
};

// ─── Partner webhook (firm / branch scoped) ─────────────────────
// Partner integrations live on the firm (or a branch override), not per-advisor. Advisors
// inherit them via the firm→branch→advisor resolution on the server.

export type WebhookScope =
  | { kind: 'firm'; firmId: string }
  | { kind: 'orgUnit'; orgUnitId: string };

const webhookBasePath = (scope: WebhookScope): string =>
  scope.kind === 'firm' ? `/api/firms/${scope.firmId}/webhook` : `/api/org-units/${scope.orgUnitId}/webhook`;

export const getWebhookConfig = async (token: string, scope: WebhookScope): Promise<PartnerWebhookConfig | null> => {
  const client = createAuthorizedClient(token);
  const response = await client.get(webhookBasePath(scope));
  return response.data.webhook;
};

export const updateWebhookConfig = async (
  token: string,
  scope: WebhookScope,
  updates: PartnerWebhookUpdate
): Promise<PartnerWebhookConfig | null> => {
  const client = createAuthorizedClient(token);
  const response = await client.put(webhookBasePath(scope), updates);
  return response.data.webhook;
};

export const sendWebhookTest = async (token: string, scope: WebhookScope): Promise<WebhookTestResult> => {
  const client = createAuthorizedClient(token);
  const response = await client.post(`${webhookBasePath(scope)}/test`);
  return response.data;
};
